import React, { useEffect, useState } from "react";
import {
  Card,
  FormControlLabel,
  Grid,
  Icon,
  Radio,
  Tooltip,
  TextField,
  MenuItem,
  RadioGroup,
  Select,
  FormControl,
  Autocomplete,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import {
  STATUS,
  hasAccess,
  activityTypeOptions,
} from "components/common/constant";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
  autoCompleteProps,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import { ArrowDropDown } from "@mui/icons-material";
import MDInput from "components/MDInput";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { useDispatch, useSelector } from "react-redux";
import {
  getMicroActivityList,
  editMicroActivity,
  addMicroActivity,
  deleteMicroActivity,
  getMicroActivityDetailsById,
  importMicroActivity,
  exportMicroActivityData,
} from "actions/microActivity";
import { microActivityList } from "constants/microActivity";
import { getSubActivityList } from "actions/subActivity";
import { subActivityList } from "constants/subActivity";
import { DEFAULT_RECORDS_PER_PAGE } from "components/common/constant";
import { getDepartmentList } from "actions/department";
import { departmentList } from "constants/department";
import { getStationList } from "actions/station";
import { stationList } from "constants/station";
import { getSubActivityUniqueList } from "actions/subActivity";

const initialFields = {
  department: { value: "", error: "", isChange: false },
  station: { value: "", error: "", isChange: false },
  subActivity: { value: "", error: "", isChange: false },
  microActivity: { value: "", error: "", isChange: false },
  activityType: { value: "", error: "", isChange: false },
  status: { value: "ACTIVE", error: "", isChange: false },
};

function MicroActivityListing() {
  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("MICROACTIVITY_MASTER", "CREATE")?.includes(
    currentRole,
  );
  const canUpdate = hasAccess("MICROACTIVITY_MASTER", "UPDATE")?.includes(
    currentRole,
  );
  const canDelete = hasAccess("MICROACTIVITY_MASTER", "DELETE")?.includes(
    currentRole,
  );

  const canShowActions = canUpdate || canDelete;

  const { notify } = useNotify();
  const dispatch = useDispatch();
  const microActivityData = useSelector(
    (state) => state.microActivity.microActivityList,
  );

  const subActivityData = useSelector(
    (state) => state.subActivity.subActivityList,
  );

  //station data
  const stationListData = useSelector((state) => state.station.stationList);
  //department data
  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );

  const subActivityUniqueData = useSelector(
    (state) => state.subActivity.subActivityUniqueList,
  );

  const departmentOptions = departmentListData?.data?.Department;
  const stationOptions = stationListData?.data?.station;
  const subActivityOptions =
    subActivityUniqueData?.data?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  //filter station option based on department
  const filteredStations = (departmentId) =>
    stationOptions?.filter((s) => s.department?.ID === departmentId) || [];

  const filteredSubActivities = (stationId, activityType) =>
    subActivityData?.data?.subActivities?.filter(
      (s) =>
        s.station?.ID === stationId &&
        (!activityType || s.activityType === activityType),
    ) || [];

  const [panelMode, setPanelMode] = useState(null); //add /edit
  const [formData, setFormData] = useState(initialFields);
  const [isUsed, setIsUsed] = useState(false);
  const [filters, setFilters] = useState({
    department: "",
    station: "",
    activityType: "",
    subActivity: "",
    microActivity: "",
    status: null,
    filterParams: [],
  });
  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [exportLoading, setExportLoading] = useState(false);

  //api to fetch micro activity list data
  useEffect(() => {
    getMicroActivityList(dispatch, pageNum, filters.filterParams);
    return () => {
      dispatch({ type: microActivityList, payload: {} });
    }; //unmount the redux data
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    getDepartmentList(dispatch);
    getStationList(dispatch);
    getSubActivityList(dispatch);
    return () => {
      dispatch({ type: stationList, payload: {} });
      dispatch({ type: departmentList, payload: {} });
      dispatch({ type: subActivityList, payload: {} });
    }; //unmount the redux data
  }, [dispatch]);

  //check the listing data state using code and message
  useEffect(() => {
    if (microActivityData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: microActivityData?.data?.microActivities?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (microActivityData?.code || microActivityData?.message) {
      setListState({
        isLoading: false,
        error:
          microActivityData?.message ||
          "Failed to load micro-activities list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [microActivityData]);

  const formattedListData = microActivityData?.data?.microActivities?.map(
    (item, index) => ({
      id: item.ID,
      microActivity: item?.name || "-",
      status: item?.status || "-",
      subActivityName: item?.subActivity?.name || "-",
      activityType:
        activityTypeOptions.find(
          (type) => type.value === item?.subActivity?.activityType,
        )?.label || "-",
      subActivityId: item?.subActivity?.ID || null,
      stationName: item?.subActivity?.station?.name || "-",
      stationId: item?.subActivity?.station?.ID || null,
      departmentName: item?.subActivity?.station?.department?.name || "-",
      departmentId: item?.subActivity?.station?.department?.ID || null,
      srNo:
        (pageNum - 1) *
          (microActivityData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    }),
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
        isChange: true,
      },
    }));
  };

  const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData)
        .filter(([, field]) => field?.isChange)
        .map(([key, field]) => {
          if (key === "microActivity") return ["name", field.value];
          if (key === "status") return ["status", field.value];
          if (key === "subActivity") return ["subActivityId", field.value?.ID];
          if (key === "activityType")
            return ["activityType", field.value?.value];
          return null;
        })
        .filter(Boolean),
    );

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const isEdit = panelMode === "edit";
    let bodyData = {
      name: formData.microActivity.value,
      status: formData.status.value,
      subActivityID: formData.subActivity.value?.ID,
    };

    if (isEdit) {
      const editedData = getEditedBodyData(formData);
      if (Object.keys(editedData).length === 0) {
        notify("No changes detected", "info");
        return;
      }
      bodyData = editedData;
    }
    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      const res = isEdit
        ? await editMicroActivity(formData.id, bodyData)
        : await addMicroActivity(bodyData);

      if (res?.code === 200) {
        notify(
          res?.message ||
            `Micro-Activity ${isEdit ? "updated" : "added"} successfully`,
          "success",
        );
        closeModal();
        // pageNum === 1
           getMicroActivityList(dispatch, pageNum, filters.filterParams)
          // : setPageNum(1);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save micro-activity";

        notify(message, "error");
      }
    } catch (error) {
      notify(`Failed to ${isEdit ? "update" : "add"} micro-activity`, "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const closeModal = () => {
    setPanelMode(null);
    setFormData({
      ...initialFields,
    });
    setIsUsed(false);
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "subActivity":
        return !trimmedValue ? "Please select sub-activity" : "";

      case "microActivity":
        return !trimmedValue ? "Please enter micro-activity" : "";

      case "activityType":
        return !value?.value ? "Please select activity type" : "";

      default:
        return "";
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...formData };
    Object.keys(newData).forEach((key) => {
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setFormData(newData);
    return Object.values(newData).every((item) => item.error === "");
  };

  const openAddModal = () => {
    // e.stopPropagation();
    setPanelMode("add");
    setFormData(initialFields);
    setIsUsed(false);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const res = await getMicroActivityDetailsById(row.id);
      if (res?.code === 200) {
        const data = res?.data;
        setIsUsed(data?.isUsed ?? false);
        const selectedSubActivity =
          subActivityData?.data?.subActivities?.find(
            (s) => s.ID === data?.subActivity?.ID,
          ) ||
          data?.subActivity ||
          null;

        const selectedStation =
          data?.subActivity?.station ||
          stationListData?.data?.station?.find(
            (s) => s.ID === data?.subActivity?.station?.ID,
          ) ||
          null;

        const selectedDepartment =
          selectedStation?.department ||
          departmentListData?.data?.Department?.find(
            (d) => d.ID === selectedStation?.department?.ID,
          ) ||
          null;

        setFormData({
          id: row.id,
          department: {
            value: selectedDepartment,
            error: "",
            isChange: false,
          },
          station: {
            value: selectedStation,
            error: "",
            isChange: false,
          },
          subActivity: {
            value: selectedSubActivity,
            error: "",
            isChange: false,
          },
          microActivity: {
            value: data?.name || "",
            error: "",
            isChange: false,
          },
          activityType: {
            value:
              activityTypeOptions.find(
                (item) => item.value === data?.subActivity?.activityType,
              ) || null,
            error: "",
            isChange: false,
          },
          status: {
            value: data?.status || "ACTIVE",
            error: "",
            isChange: false,
          },
        });
      } else {
        notify(res?.message || "Failed to fetch details", "error");
        closeModal();
      }
    } catch (error) {
      notify("Error fetching micro activity details", "error");
      closeModal();
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleDelete = (row) => {
    showAlert({
      title: `Are you sure you want to delete this micro-activity?`,
      message: `Micro-Activity "${row.microActivity}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));
          const res = await deleteMicroActivity(row.id);
          if (res?.code === 200) {
            notify(
              res?.message || "Micro-activity deleted successfully",
              "success",
            );
            pageNum === 1
              ? getMicroActivityList(dispatch, pageNum, filters.filterParams)
              : setPageNum(1);
          } else {
            notify(res?.message || "Failed to delete micro-activity", "error");
          }
        } catch (error) {
          notify("Something went wrong while deleting micro-activity", "error");
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },
      onCancel: () => {},
    });
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    return (
      <MDBadge
        badgeContent={status}
        color={status === "ACTIVE" ? "success" : "secondary"}
        variant="gradient"
        size="sm"
        circular
      />
    );
  };

  const columns = [
    {
      Header: "Sr No.",
      accessor: "srNo",
      align: "left",
      width: "85px",
    },
    {
      Header: "Department",
      accessor: "departmentName",
      align: "left",
      width: "170px",
      Cell: (cell) => wrapCell(cell.value, "270px"),
    },
    {
      Header: "Station",
      accessor: "stationName",
      align: "left",
      Cell: (cell) => wrapCell(cell.value, "270px"),
    },
    {
      Header: "Activity Type",
      accessor: "activityType",
      align: "left",
      width: "180px",
      Cell: (cell) => cell.value || "-",
    },
    {
      Header: "Sub Activity",
      accessor: "subActivityName",
      align: "left",
      width: "170px",
      Cell: (cell) => wrapCell(cell.value, "270px"),
    },
    {
      Header: "Micro Activity",
      accessor: "microActivity",
      width: "190px",
      align: "left",
      Cell: (cell) => wrapCell(cell.value, "270px"),
    },
    {
      Header: "Status",
      accessor: "status",
      width: "125px",
      align: "center",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },
    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption" mx={0.5}>
                      |
                    </MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.department) {
      filterParams.push({
        field: "DepartmentID",
        condition: "eq",
        filterValues: [String(filters.department)],
      });
    }

    if (filters.activityType) {
      filterParams.push({
        field: "ActivityType",
        condition: "eq",
        filterValues: [filters.activityType],
      });
    }

    if (filters.station) {
      filterParams.push({
        field: "StationID",
        condition: "eq",
        filterValues: [String(filters.station)],
      });
    }

    if (filters.microActivity?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.microActivity.trim()],
      });
    }

    if (filters.subActivity) {
      filterParams.push({
        field: "SubActivityID",
        condition: "eq",
        filterValues: [String(filters.subActivity)],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newfilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilters((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setFilters({
      department: "",
      station: "",
      activityType: "",
      subActivity: "",
      microActivity: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const downloadMicroActivityCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportMicroActivityData(filters.filterParams);
      
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "MicroActivity.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message || "MicroActivity file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download MicroActivity file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const downloadSampleCsv = () => {
    const csvContent = `ID,Department Name,Station Name,SubActivity Name,Microactivity Name
1,Winding,WDG-05,Wiring Check,abc
2,Dispatch,DISP-01,Valve Inspection,def
3,Tanking,TP-01,Battery Testing,ghi`;

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "sample_microactivity.csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // only csv validation
    if (!file.name.toLowerCase().endsWith(".csv")) {
      notify("Please upload a valid CSV file", "error");
      e.target.value = null;
      return;
    }

    try {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
      }));

      const formData = new FormData();
      formData.append("file", file);

      const res = await importMicroActivity(formData);

      if (res?.code === 200) {
        notify(
          res?.message || "Micro-Activity CSV imported successfully",
          "success",
        );

        // refresh listing
        getMicroActivityList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to import micro-activity CSV";

        notify(message, "error");
      }
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while importing CSV",
        "error",
      );
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));

      e.target.value = null;
    }
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Micro-Activity
                </MDTypography>

                <MDBox display="flex" gap={2}>
                  {canCreate && (
                    <>
                      <MDButton
                        variant="gradient"
                        color="light"
                        startIcon={<Icon>file_download</Icon>}
                        sx={{
                          py: 1.2,
                          px: 3,
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                        onClick={downloadSampleCsv}
                      >
                        Sample CSV
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="light"
                        startIcon={<Icon>file_upload</Icon>}
                        sx={{
                          py: 1.2,
                          px: 3,
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                        onClick={downloadMicroActivityCSV}
                      >
                        {exportLoading ? (
                          <>
                            <i className="fa fa-spinner fa-spin mr-2" />
                            Exporting...
                          </>
                        ) : (
                          "Export Micro Activity"
                        )}
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="light"
                        component="label"
                        startIcon={<Icon>download</Icon>}
                        sx={{
                          py: 1.2,
                          px: 3,
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        Import CSV
                        <input
                          type="file"
                          hidden
                          accept=".csv"
                          onChange={handleCsvUpload}
                        />
                      </MDButton>
                      <MDButton
                        type="button"
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          py: 1.2,
                          px: 3,
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                          textTransform: "none",
                        }}
                        onClick={openAddModal}
                      >
                        Add Micro-Activity
                      </MDButton>
                    </>
                  )}
                </MDBox>
              </MDBox>

              {/* Filters */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={departmentOptions || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        departmentOptions?.find(
                          (dept) => dept.ID === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.ID || "",
                          station: "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select department"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={stationListData?.data?.station || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        stationListData?.data?.station?.find(
                          (stat) => stat.ID === filters.station,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          station: newValue?.ID || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select station" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={activityTypeOptions || []}
                      getOptionLabel={(option) => option.label || ""}
                      value={
                        activityTypeOptions?.find(
                          (type) => type.value === filters.activityType,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          activityType: newValue?.value || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select activity type"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={subActivityOptions}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        subActivityOptions.find(
                          (subActivity) =>
                            subActivity.id === filters.subActivity,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          subActivity: newValue?.id || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select sub activity"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      placeholder="Search by micro activity"
                      size="small"
                      value={filters.microActivity}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          microActivity: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        IconComponent={ArrowDropDown}
                        renderValue={(selected) => {
                          if (!selected)
                            return (
                              <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                                 Select Status
                              </span>
                            );

                          return renderStatusBadge(selected);
                        }}
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.label} value={status.label}>
                            {renderStatusBadge(status.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>
                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox
                px={3}
                py={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {microActivityData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography color="error" variant="body2">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="20vh"
                  >
                    <MDTypography color="text" variant="body2">
                      No micro-activity found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={formattedListData || []}
                    isSorted
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (microActivityData?.data?.totalRecords || 0) /
                        (microActivityData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <CenterPopup isOpen={panelMode !== null} onClose={closeModal}>
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={0}>
              {panelMode === "edit"
                ? "Edit Micro-Activity"
                : "Add Micro-Activity"}
            </MDTypography>

            {panelMode === "edit" && isUsed && (
              <Grid item xs={12} sx={{ mb: 1 }}>
                <MDTypography
                  variant="caption"
                  sx={{
                    color: "#fc6e0f",
                  }}
                >
                  <b>Note:</b> This micro-activity is used in timesheets, so
                  Department, Station, Activity Type, Sub-Activity and
                  Micro-Activity cannot be edited.
                </MDTypography>
              </Grid>
            )}

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RequiredLabel required>Department</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disabled={panelMode === "edit" && isUsed}
                    options={departmentOptions || []}
                    getOptionLabel={(o) => o.name || ""}
                    value={formData.department.value || null}
                    onChange={(e, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        department: { value: newValue, error: "" },
                        station: { value: "", error: "" },
                        subActivity: { value: "", error: "" },
                        microActivity: { value: "", error: "" },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Department" />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Station</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disabled={
                      !formData.department.value ||
                      (panelMode === "edit" && isUsed)
                    }
                    options={filteredStations(formData.department.value?.ID)}
                    getOptionLabel={(o) => o.name || ""}
                    value={formData.station.value || null}
                    onChange={(e, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        station: { value: newValue, error: "" },
                        subActivity: { value: "", error: "" },
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select Station" />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Activity Type</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disabled={panelMode === "edit" && isUsed}
                    options={activityTypeOptions}
                    getOptionLabel={(option) => option.label || ""}
                    value={formData.activityType.value}
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        activityType: {
                          value: newValue || null,
                          error: "",
                          isChange: true,
                        },
                        subActivity: {
                          value: "",
                          error: "",
                          isChange: true,
                        },
                      }));
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select activity type"
                        error={!!formData.activityType.error}
                        helperText={formData.activityType.error}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Sub-Activity</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disabled={
                      !formData.station.value ||
                      (panelMode === "edit" && isUsed)
                    }
                    options={filteredSubActivities(
                      formData.station.value?.ID,
                      formData.activityType.value?.value,
                    )}
                    getOptionLabel={(o) => o.name || ""}
                    value={formData.subActivity.value || null}
                    onChange={(e, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        subActivity: {
                          value: newValue,
                          error: "",
                          isChange: true,
                        },
                        ...(panelMode !== "edit" && {
                          microActivity: {
                            value: "",
                            error: "",
                            isChange: false,
                          },
                        }),
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Sub Activity"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Micro-Activity</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="microActivity"
                    placeholder="Enter micro-activity"
                    value={formData.microActivity.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!formData.microActivity.error}
                    fullWidth
                    disabled={
                      !formData.subActivity.value ||
                      (panelMode === "edit" && isUsed)
                    }
                    inputProps={{ maxLength: 100 }}
                  />
                  {formData.microActivity.error && (
                    <MDTypography variant="caption" color="error">
                      {formData.microActivity.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={formData.status.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                    >
                      {listState.showLoaderOnClick ? (
                        <>
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        </>
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default MicroActivityListing;
